import { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
  Loader2, LogOut, ShieldCheck, PlusCircle, Trash2, PencilLine,
  ImagePlus, ArrowLeft, Eye, EyeOff, Lightbulb, Check,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const inputCls =
  "w-full rounded-xl border border-navy/10 bg-white px-4 py-3.5 text-sm text-navy placeholder:text-charcoal/40 outline-none transition-colors duration-200 focus:border-mint focus:ring-2 focus:ring-mint/20";

const slugify = (s) =>
  s.toLowerCase().trim().replace(/[^a-z0-9\s-]/g, "").replace(/[\s_]+/g, "-").replace(/-+/g, "-").slice(0, 80);

const EMPTY = { title: "", slug: "", excerpt: "", content: "", image: "", meta_title: "", meta_description: "", status: "draft" };

const BLOG_IDEAS = [
  { t: "Bike Insurance Renewal in Noida: A 5-Minute Guide", e: "Policy expiring? Here's how to renew your two-wheeler insurance in Noida without paperwork stress — and what to check before you pay." },
  { t: "Car Insurance Add-Ons Explained: Which 3 Are Worth It for NCR Roads", e: "Zero depreciation, engine protect, roadside assistance — what each add-on actually does and which ones make sense for daily NCR driving." },
  { t: "Monsoon and Your Car: Why Engine Protect Matters in Greater Noida", e: "Waterlogging can destroy an engine — and basic insurance won't pay for it. A simple guide to the engine protect add-on before the rains." },
  { t: "What Is IDV? The One Number That Decides Your Car's Claim", e: "IDV sounds technical but it's just your car's current value. Learn how setting it right affects both your premium and your payout." },
  { t: "No Claim Bonus: How Claim-Free Years Cut Your Motor Premium", e: "Your careful driving earns a growing discount — up to 50%. How NCB works, how to protect it, and what happens after a claim." },
  { t: "Commercial Vehicle Insurance for Taxi and Goods Owners in Noida", e: "Taxi and GCV insurance work differently from private car cover. What passenger and goods vehicle owners should know before renewing." },
  { t: "Health Insurance for Young Families in Greater Noida West: Where to Start", e: "First health plan for your family? A simple starting framework — how much cover, floater vs individual, and what to check first." },
  { t: "Waiting Periods in Health Insurance, Explained in 5 Minutes", e: "Why some illnesses aren't covered immediately, the three types of waiting periods, and why buying early is the smartest move." },
  { t: "Family Floater vs Individual Health Plans: Which Fits Your Family?", e: "One shared cover or separate covers for each member? A plain-language comparison to help your family decide." },
  { t: "Cashless Hospitalisation: How It Works at Noida-Area Hospitals", e: "What 'cashless' really means, how network hospitals work, and the one phone call you should make before planned admission." },
  { t: "Term Insurance in Your 30s: Why Every Year You Wait Costs More", e: "Term premiums lock at your entry age. A simple look at why a 28-year-old pays so much less than a 38-year-old for the same cover." },
  { t: "How Much Term Cover Does Your Family Actually Need?", e: "The 10-15x income rule, adjusted for loans and goals — a practical way to arrive at your family's real protection number." },
  { t: "Term Insurance Riders Explained: Accidental Death, Critical Illness, Premium Waiver", e: "Riders are small add-ons that can make a big difference. What the three most common term riders do and when they're worth it." },
  { t: "Life Insurance for the Self-Employed in Noida: A Simple Guide", e: "No employer benefits? Here's how freelancers and business owners can build their own family's financial safety net." },
  { t: "Fire Insurance for Small Shops: Protecting Your Stock and Premises", e: "One fire can wipe out years of hard work. What fire insurance covers for shops and small units, and what affects the premium." },
  { t: "Single Transit Insurance: What Delivery and Traders Should Know", e: "Sending goods occasionally? How single transit cover protects one specific shipment — and when an annual marine policy makes more sense." },
  { t: "Workmen Compensation Insurance: What Every Noida Employer Should Know", e: "If workers get injured on the job, the liability is yours. What this cover does, who needs it, and why it's responsible business." },
  { t: "Travel Insurance for Your Next Holiday: What's Actually Covered", e: "Medical emergencies, lost bags, delayed flights — a simple walkthrough of what travel insurance does before your next trip." },
  { t: "Schengen Visa Insurance: The €30,000 Rule Explained Simply", e: "Applying for a Schengen visa? Travel insurance is mandatory — here's the exact coverage requirement and how to meet it correctly." },
  { t: "Insurance Glossary: 15 Terms Every Policyholder Should Know", e: "Premium, sum insured, deductible, nominee, NCB, IDV, co-pay — the most common insurance words explained in one line each." },
];

export default function AdminBlog() {
  const [token, setToken] = useState(() => localStorage.getItem("pb_admin_token"));
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [posts, setPosts] = useState(null);
  const [editing, setEditing] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [ideasDone, setIdeasDone] = useState(() => JSON.parse(localStorage.getItem("pb_blog_ideas") || "[]"));
  const fileRef = useRef(null);

  const toggleIdea = (i) => {
    setIdeasDone((done) => {
      const next = done.includes(i) ? done.filter((x) => x !== i) : [...done, i];
      localStorage.setItem("pb_blog_ideas", JSON.stringify(next));
      return next;
    });
  };

  const startIdea = (idea) => {
    setEditing({ ...EMPTY, title: idea.t, excerpt: idea.e, meta_title: `${idea.t} | PocketBima` });
    window.scrollTo({ top: 0, behavior: "instant" });
  };

  const headers = { Authorization: `Bearer ${token}` };

  const load = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/admin/blog`, { headers });
      setPosts(data);
    } catch (e) {
      if (e.response?.status === 401) {
        localStorage.removeItem("pb_admin_token");
        setToken(null);
      } else {
        toast.error("Could not load posts");
      }
    }
  }, [token]);

  useEffect(() => {
    if (token) load();
  }, [token, load]);

  const login = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      const { data } = await axios.post(`${API}/auth/login`, { email, password });
      localStorage.setItem("pb_admin_token", data.token);
      setToken(data.token);
    } catch (err) {
      setError(typeof err.response?.data?.detail === "string" ? err.response.data.detail : "Login failed");
    } finally {
      setLoading(false);
    }
  };

  const uploadImage = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await axios.post(`${API}/admin/blog/upload`, fd, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setEditing((ed) => ({ ...ed, image: data.url }));
      toast.success("Photo uploaded");
    } catch (e) {
      toast.error(typeof e.response?.data?.detail === "string" ? e.response.data.detail : "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const save = async (status) => {
    const payload = { ...editing, status: status || editing.status };
    if (!payload.slug) payload.slug = slugify(payload.title);
    try {
      if (editing.id) {
        await axios.put(`${API}/admin/blog/${editing.id}`, payload, { headers });
      } else {
        await axios.post(`${API}/admin/blog`, payload, { headers });
      }
      toast.success(payload.status === "published" ? "Post published!" : "Draft saved");
      setEditing(null);
      load();
    } catch (e) {
      toast.error(typeof e.response?.data?.detail === "string" ? e.response.data.detail : "Save failed");
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this post permanently?")) return;
    await axios.delete(`${API}/admin/blog/${id}`, { headers });
    toast.success("Post deleted");
    load();
  };

  if (!token) {
    return (
      <main className="hero-grid-bg bg-ice py-24" data-testid="blog-admin-login">
        <div className="mx-auto max-w-md px-5">
          <div className="rounded-[2rem] border border-navy/10 bg-white p-8 shadow-soft sm:p-10">
            <div className="flex justify-center">
              <img src="/images/logo.webp" alt="Pocket Bima Insurance Service" width="800" height="667" className="h-24 w-auto" />
            </div>
            <h1 className="mt-6 text-center font-display text-2xl font-extrabold tracking-tight text-navy">Blog Manager</h1>
            <p className="mt-2 text-center text-sm text-charcoal/70">Sign in to write and publish posts.</p>
            <form onSubmit={login} className="mt-8 space-y-4" data-testid="blog-admin-login-form">
              <input type="email" required placeholder="Email" value={email} onChange={(e) => setEmail(e.target.value)} className={inputCls} data-testid="blog-admin-email" aria-label="Email" />
              <input type="password" required placeholder="Password" value={password} onChange={(e) => setPassword(e.target.value)} className={inputCls} data-testid="blog-admin-password" aria-label="Password" />
              {error && <p className="rounded-xl bg-red-50 px-4 py-3 text-sm font-medium text-red-600" data-testid="blog-admin-error">{error}</p>}
              <button type="submit" disabled={loading} className="flex w-full items-center justify-center gap-2 rounded-full bg-mint py-4 text-base font-bold text-white shadow-mint transition-all hover:-translate-y-0.5 hover:bg-mint-600 disabled:opacity-60" data-testid="blog-admin-login-submit">
                {loading ? <Loader2 className="h-5 w-5 animate-spin" /> : <ShieldCheck className="h-4 w-4" />}
                {loading ? "Signing in..." : "Sign In"}
              </button>
            </form>
          </div>
        </div>
      </main>
    );
  }

  if (editing) {
    return (
      <main className="min-h-[70vh] bg-ice py-14" data-testid="blog-editor">
        <div className="mx-auto max-w-3xl px-5 sm:px-8">
          <button onClick={() => setEditing(null)} className="flex items-center gap-2 text-sm font-bold text-navy transition-colors hover:text-mint-600" data-testid="blog-editor-back">
            <ArrowLeft className="h-4 w-4" /> Back to posts
          </button>
          <h1 className="mt-4 font-display text-3xl font-extrabold tracking-tight text-navy">
            {editing.id ? "Edit Post" : "New Post"}
          </h1>
          <div className="mt-8 space-y-5 rounded-[2rem] border border-navy/10 bg-white p-7 shadow-soft sm:p-9">
            <div>
              <label className="text-xs font-bold uppercase tracking-[0.15em] text-skycyan-600">Title</label>
              <input value={editing.title} onChange={(e) => setEditing({ ...editing, title: e.target.value })} onBlur={() => !editing.slug && setEditing((ed) => ({ ...ed, slug: slugify(ed.title) }))} placeholder="e.g. Third-Party vs Comprehensive Insurance" className={`${inputCls} mt-2`} data-testid="blog-title-input" />
            </div>
            <div>
              <label className="text-xs font-bold uppercase tracking-[0.15em] text-skycyan-600">Slug (URL)</label>
              <input value={editing.slug} onChange={(e) => setEditing({ ...editing, slug: slugify(e.target.value) })} placeholder="auto-generated-from-title" className={`${inputCls} mt-2`} data-testid="blog-slug-input" />
              <p className="mt-1.5 pl-1 text-xs text-charcoal/50">pocketbima…/blog/{editing.slug || "your-post-slug"}</p>
            </div>
            <div>
              <label className="text-xs font-bold uppercase tracking-[0.15em] text-skycyan-600">Short Excerpt</label>
              <textarea value={editing.excerpt} onChange={(e) => setEditing({ ...editing, excerpt: e.target.value })} rows={2} placeholder="One or two lines shown on the blog listing" className={`${inputCls} mt-2`} data-testid="blog-excerpt-input" />
            </div>
            <div>
              <label className="text-xs font-bold uppercase tracking-[0.15em] text-skycyan-600">Cover Photo</label>
              <div className="mt-2 flex flex-wrap items-center gap-3">
                <button onClick={() => fileRef.current?.click()} disabled={uploading} className="flex items-center gap-2 rounded-xl border-2 border-dashed border-navy/20 px-5 py-3 text-sm font-bold text-navy transition-colors hover:border-mint hover:text-mint-600" data-testid="blog-image-upload">
                  {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <ImagePlus className="h-4 w-4" />}
                  {uploading ? "Uploading..." : "Upload Photo"}
                </button>
                <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={(e) => uploadImage(e.target.files?.[0])} />
                <input value={editing.image} onChange={(e) => setEditing({ ...editing, image: e.target.value })} placeholder="…or paste an image URL" className={`${inputCls} flex-1 min-w-[200px]`} data-testid="blog-image-url" />
              </div>
              {editing.image && <img src={editing.image} alt="Cover preview" className="mt-3 aspect-video w-full rounded-2xl border border-navy/10 object-cover" data-testid="blog-image-preview" />}
            </div>
            <div>
              <label className="text-xs font-bold uppercase tracking-[0.15em] text-skycyan-600">Content</label>
              <textarea value={editing.content} onChange={(e) => setEditing({ ...editing, content: e.target.value })} rows={14} placeholder="Write your guide here. Separate paragraphs with a blank line." className={`${inputCls} mt-2 leading-relaxed`} data-testid="blog-content-input" />
            </div>
            <div className="grid gap-5 sm:grid-cols-2">
              <div>
                <label className="text-xs font-bold uppercase tracking-[0.15em] text-skycyan-600">Meta Title (SEO)</label>
                <input value={editing.meta_title} onChange={(e) => setEditing({ ...editing, meta_title: e.target.value })} placeholder="Defaults to post title" className={`${inputCls} mt-2`} data-testid="blog-meta-title" />
              </div>
              <div>
                <label className="text-xs font-bold uppercase tracking-[0.15em] text-skycyan-600">Meta Description (SEO)</label>
                <input value={editing.meta_description} onChange={(e) => setEditing({ ...editing, meta_description: e.target.value })} placeholder="Defaults to excerpt" className={`${inputCls} mt-2`} data-testid="blog-meta-desc" />
              </div>
            </div>
            <div className="flex flex-wrap gap-3 pt-2">
              <button onClick={() => save("draft")} className="flex items-center gap-2 rounded-full border-2 border-navy/15 px-6 py-3 text-sm font-bold text-navy transition-colors hover:border-navy" data-testid="blog-save-draft">
                <EyeOff className="h-4 w-4" /> Save as Draft
              </button>
              <button onClick={() => save("published")} className="flex items-center gap-2 rounded-full bg-mint px-6 py-3 text-sm font-bold text-white shadow-mint transition-all hover:-translate-y-0.5 hover:bg-mint-600" data-testid="blog-publish-button">
                <Eye className="h-4 w-4" /> {editing.status === "published" ? "Update (Published)" : "Publish Now"}
              </button>
            </div>
          </div>
        </div>
      </main>
    );
  }

  return (
    <main className="min-h-[70vh] bg-ice py-14" data-testid="blog-admin">
      <div className="mx-auto max-w-4xl px-5 sm:px-8">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div>
            <h1 className="font-display text-3xl font-extrabold tracking-tight text-navy" data-testid="blog-admin-heading">Blog Manager</h1>
            <p className="mt-1 text-sm text-charcoal/70">Write guides, publish them, and help customers find you on Google.</p>
          </div>
          <div className="flex gap-3">
            <Link to="/admin" className="flex items-center gap-2 rounded-full border border-navy/15 bg-white px-5 py-2.5 text-sm font-bold text-navy transition-colors hover:border-mint hover:text-mint-600" data-testid="blog-admin-back">
              <ArrowLeft className="h-4 w-4" /> Dashboard
            </Link>
            <button onClick={() => setEditing({ ...EMPTY })} className="flex items-center gap-2 rounded-full bg-mint px-5 py-2.5 text-sm font-bold text-white shadow-mint transition-all hover:-translate-y-0.5 hover:bg-mint-600" data-testid="blog-new-button">
              <PlusCircle className="h-4 w-4" /> New Post
            </button>
          </div>
        </div>

        <div className="mt-8 space-y-4">
          {posts === null && <div className="flex justify-center py-16"><Loader2 className="h-8 w-8 animate-spin text-mint" /></div>}
          {posts?.length === 0 && (
            <p className="rounded-2xl border border-dashed border-navy/20 bg-white p-14 text-center font-semibold text-charcoal/60" data-testid="blog-admin-empty">
              No posts yet — click "New Post" to write your first guide.
            </p>
          )}
          {posts?.map((p) => (
            <div key={p.id} className="flex flex-wrap items-center gap-4 rounded-2xl border border-navy/10 bg-white p-5 shadow-soft" data-testid={`blog-admin-card-${p.slug}`}>
              {p.image && <img src={p.image} alt="" className="h-14 w-20 rounded-xl object-cover" />}
              <div className="min-w-0 flex-1">
                <div className="flex flex-wrap items-center gap-2.5">
                  <h3 className="truncate font-display text-base font-bold text-navy">{p.title}</h3>
                  <span className={`rounded-full px-3 py-1 text-xs font-bold ${p.status === "published" ? "bg-mint-50 text-mint-700" : "bg-amber-50 text-amber-600"}`} data-testid="blog-admin-status">
                    {p.status === "published" ? "Published" : "Draft"}
                  </span>
                </div>
                <p className="mt-1 text-xs text-charcoal/50">
                  /blog/{p.slug} · {new Date(p.created_at).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" })}
                </p>
              </div>
              <div className="flex gap-2">
                <button onClick={() => setEditing(p)} className="flex items-center gap-1.5 rounded-full bg-navy px-4 py-2 text-xs font-bold text-white transition-colors hover:bg-navy-700" data-testid={`blog-edit-${p.slug}`}>
                  <PencilLine className="h-3.5 w-3.5" /> Edit
                </button>
                <button onClick={() => remove(p.id)} className="flex items-center gap-1.5 rounded-full border border-red-200 px-4 py-2 text-xs font-bold text-red-500 transition-colors hover:bg-red-50" data-testid={`blog-delete-${p.slug}`}>
                  <Trash2 className="h-3.5 w-3.5" /> Delete
                </button>
              </div>
            </div>
          ))}
        </div>

        <section className="mt-14" data-testid="blog-ideas-section">
          <div className="flex items-center gap-3">
            <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-navy">
              <Lightbulb className="h-5 w-5 text-mint" />
            </span>
            <div>
              <h2 className="font-display text-xl font-extrabold tracking-tight text-navy" data-testid="blog-ideas-heading">
                Blog Ideas — 20 Topics Ready to Write
              </h2>
              <p className="text-xs text-charcoal/60">Local, search-friendly topics. Tick them off as you publish.</p>
            </div>
            <span className="ml-auto rounded-full bg-mint-50 px-3 py-1 text-xs font-bold text-mint-700" data-testid="blog-ideas-count">
              {ideasDone.length}/{BLOG_IDEAS.length}
            </span>
          </div>
          <div className="mt-5 space-y-2.5">
            {BLOG_IDEAS.map((idea, i) => {
              const done = ideasDone.includes(i);
              return (
                <div
                  key={i}
                  className={`flex items-start gap-3.5 rounded-2xl border p-4 transition-all duration-200 ${
                    done ? "border-mint/30 bg-mint-50" : "border-navy/10 bg-white"
                  }`}
                  data-testid={`blog-idea-${i}`}
                >
                  <button
                    onClick={() => toggleIdea(i)}
                    aria-label="Mark idea as done"
                    className={`mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full border-2 transition-colors duration-200 ${
                      done ? "border-mint bg-mint" : "border-navy/20 hover:border-mint"
                    }`}
                    data-testid={`blog-idea-check-${i}`}
                  >
                    {done && <Check className="h-3.5 w-3.5 text-white" strokeWidth={3} />}
                  </button>
                  <div className="min-w-0 flex-1">
                    <p className={`text-sm font-bold leading-snug ${done ? "text-charcoal/40 line-through" : "text-navy"}`}>
                      {idea.t}
                    </p>
                    <p className={`mt-1 text-xs leading-relaxed ${done ? "text-charcoal/35" : "text-charcoal/60"}`}>
                      {idea.e}
                    </p>
                  </div>
                  {!done && (
                    <button
                      onClick={() => startIdea(idea)}
                      className="shrink-0 rounded-full bg-navy px-4 py-2 text-xs font-bold text-white transition-colors hover:bg-mint"
                      data-testid={`blog-idea-use-${i}`}
                    >
                      Use This Idea
                    </button>
                  )}
                </div>
              );
            })}
          </div>
        </section>
      </div>
    </main>
  );
}
